import React, { useState } from 'react';
import { api, OverlapSuggestion, PublicUser } from './api';

const DURATIONS = [15, 30, 45, 60];

function fmtWindow(startIso: string, endIso: string): string {
  try {
    const s = new Date(startIso);
    const e = new Date(endIso);
    const day = s.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    const t = (d: Date) => d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
    return `${day} · ${t(s)}–${t(e)}`;
  } catch { return startIso; }
}

/** "Find a time" — pick people + duration, get slots inside everyone's working hours and
 *  around their connected calendars, then create + send the invite. */
export function FindTimeModal({ workspaceId, members, defaultIds, onClose }: {
  workspaceId: string; members: PublicUser[]; defaultIds: string[]; onClose: () => void;
}) {
  const [picked, setPicked] = useState<string[]>(defaultIds.length ? defaultIds : members.map((m) => m.id).slice(0, 8));
  const [duration, setDuration] = useState(30);
  const [suggestions, setSuggestions] = useState<OverlapSuggestion[] | null>(null);
  const [meta, setMeta] = useState<{ withCal: number; skipped: number; considered: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [scheduling, setScheduling] = useState('');
  const [scheduled, setScheduled] = useState<Record<string, string>>({}); // start_utc -> link|'ok'

  const toggle = (id: string) => setPicked((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));

  const find = async () => {
    if (picked.length === 0) return;
    setLoading(true); setSuggestions(null);
    try {
      const r: any = await api.overlap(workspaceId, { user_ids: picked, duration_min: duration, within_days: 14 });
      setSuggestions(r.suggestions || []);
      setMeta({ withCal: r.with_calendar ?? 0, skipped: r.skipped_no_tz ?? 0, considered: r.considered ?? 0 });
    } catch (e: any) { window.alert(e?.message || 'Could not find times.'); }
    finally { setLoading(false); }
  };

  const schedule = async (s: OverlapSuggestion) => {
    const title = window.prompt('Meeting title', 'Meeting');
    if (title === null) return;
    setScheduling(s.start_utc);
    try {
      const r = await api.scheduleEvent({ start_utc: s.start_utc, end_utc: s.end_utc, title: title || 'Meeting', attendee_ids: picked });
      setScheduled((m) => ({ ...m, [s.start_utc]: r.link || 'ok' }));
    } catch (e: any) { window.alert(e?.message || 'Could not schedule — connect a calendar in Settings first.'); }
    finally { setScheduling(''); }
  };

  return (
    <div className="fx-modal-backdrop" onClick={onClose}>
      <div className="fx-modal" style={{ width: 'min(94vw, 540px)', maxHeight: '88vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div className="fx-login-title">Find a time</div>
        <div className="fx-login-note" style={{ textAlign: 'left', marginTop: 0 }}>
          Pick who’s meeting — we suggest slots inside everyone’s working hours, and around their
          calendars where connected. Each person just needs a <strong>time zone + working hours</strong> set
          (Settings → Status &amp; availability); connecting a calendar is optional and only refines things.
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, margin: '12px 0' }}>
          {members.map((m) => {
            const on = picked.includes(m.id);
            return (
              <button key={m.id} type="button" onClick={() => toggle(m.id)}
                style={{ fontSize: 12.5, fontWeight: 600, padding: '4px 12px', borderRadius: 999,
                  border: `1px solid ${on ? 'var(--fx-accent,#f7b500)' : 'rgba(128,128,128,0.32)'}`,
                  background: on ? 'var(--fx-accent,#f7b500)' : 'transparent', color: on ? '#1a1200' : 'inherit',
                  cursor: 'pointer', fontFamily: 'inherit' }}>
                {m.name || m.email}
              </button>
            );
          })}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13 }}>Duration</span>
          {DURATIONS.map((d) => {
            const on = duration === d;
            return (
              <button key={d} type="button" onClick={() => setDuration(d)}
                style={{ fontSize: 12.5, fontWeight: 600, padding: '4px 12px', borderRadius: 999,
                  border: `1px solid ${on ? 'var(--fx-accent,#f7b500)' : 'rgba(128,128,128,0.32)'}`,
                  background: on ? 'var(--fx-accent,#f7b500)' : 'transparent', color: on ? '#1a1200' : 'inherit',
                  cursor: 'pointer', fontFamily: 'inherit' }}>
                {d}m
              </button>
            );
          })}
          <button type="button" className="fx-sendbtn" onClick={find} disabled={loading || picked.length === 0}>{loading ? 'Finding…' : 'Find times'}</button>
        </div>
        {suggestions && (
          <div>
            <div className="fx-login-note" style={{ textAlign: 'left', fontSize: 12 }}>
              {suggestions.length === 0
                ? (meta && meta.skipped > 0
                    ? `No shared slots — ${meta.skipped} ${meta.skipped === 1 ? 'person hasn’t' : 'people haven’t'} set a time zone + working hours yet (Settings → Status & availability), so only ${meta.considered} could be scheduled around. Ask them to set it, or try a shorter duration.`
                    : (meta && meta.considered === 0
                        ? 'No one selected has a time zone set yet — set yours and theirs in Settings → Status & availability.'
                        : 'No shared slots in the next two weeks — try a shorter duration or fewer people.'))
                : `${suggestions.length} slot${suggestions.length === 1 ? '' : 's'}${meta && meta.withCal ? ` · ${meta.withCal} calendar${meta.withCal === 1 ? '' : 's'} checked` : ''}${meta && meta.skipped > 0 ? ` · ${meta.skipped} skipped (no time zone)` : ''}`}
            </div>
            {suggestions.map((s) => (
              <div key={s.start_utc} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid rgba(128,128,128,0.14)' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{fmtWindow(s.start_utc, s.end_utc)}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--fx-muted,#9092a0)' }}>{s.participants.map((p) => p.local_time).join(' · ')}</div>
                </div>
                {scheduled[s.start_utc] ? (
                  scheduled[s.start_utc] === 'ok'
                    ? <span style={{ fontSize: 12, color: 'var(--fx-good,#3ecf8e)' }}>Scheduled ✓</span>
                    : <a href={scheduled[s.start_utc]} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: 'var(--fx-good,#3ecf8e)' }}>Scheduled ✓</a>
                ) : (
                  <button type="button" className="fx-login-back" onClick={() => schedule(s)} disabled={scheduling === s.start_utc}>{scheduling === s.start_utc ? '…' : 'Schedule'}</button>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="fx-modal-actions" style={{ marginTop: 12 }}>
          <button type="button" className="fx-login-back" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
